import { ApiError, ContractError, HttpError, isRetryableSaveError } from '../../../api';
import { isInvalidRequest } from '../api/requestSchemas';
import { REFUSAL_CODES } from '../constants/vocabulary';
import type { RefusalCode } from '../constants/vocabulary';

export interface Violation {
  readonly path: string;
  readonly code: string;
  readonly message: string;
}

export type ViolationSource = 'LIBRARY_RULES' | 'REGISTRY_RULES' | 'SCHEMA';

export type SaveFailure =
  | {
      readonly kind: 'conflict';
      readonly expectedVersion: number | null;
      readonly currentVersion: number | null;
      readonly message: string | null;
    }
  | { readonly kind: 'violations'; readonly source: ViolationSource; readonly violations: readonly Violation[]; readonly message: string | null }
  | { readonly kind: 'unknownItem'; readonly keys: readonly string[]; readonly message: string | null }
  | { readonly kind: 'inUse'; readonly machines: number | null; readonly message: string | null }
  | { readonly kind: 'notFound'; readonly message: string | null }
  | { readonly kind: 'reason'; readonly problem: 'TOO_SHORT' | 'TOO_LONG'; readonly message: string | null }
  | { readonly kind: 'forbidden'; readonly message: string | null }
  | {
      readonly kind: 'other';
      readonly code: RefusalCode | null;
      readonly status: number | null;
      readonly message: string | null;
      readonly retryable: boolean;
    };

const STATUS_FORBIDDEN = 403;
const STATUS_NOT_FOUND = 404;
const STATUS_UNPROCESSABLE = 422;

const isRefusalCode = (value: string | null): value is RefusalCode =>
  value !== null && (REFUSAL_CODES as readonly string[]).includes(value);

export const refusalCodeOf = (error: unknown): RefusalCode | null => {
  if (!(error instanceof HttpError)) return null;
  return isRefusalCode(error.code) ? error.code : null;
};

const statusOf = (error: unknown): number | null => (error instanceof HttpError ? error.status : null);

export const isNotFound = (error: unknown): boolean => statusOf(error) === STATUS_NOT_FOUND;

export const isForbidden = (error: unknown): boolean => statusOf(error) === STATUS_FORBIDDEN;

export const isNoLayout = (error: unknown): boolean => refusalCodeOf(error) === 'NO_LAYOUT';

export type LoadRefusal = 'forbidden' | 'notFound';

export interface LoadOutcome {
  readonly hasData: boolean;
  readonly isError: boolean;
  readonly loadError: unknown;
}

export const loadRefusalOf = (load: LoadOutcome): LoadRefusal | null => {
  if (load.hasData || !load.isError) return null;
  if (isForbidden(load.loadError)) return 'forbidden';
  if (isNotFound(load.loadError)) return 'notFound';
  return null;
};

const serverMessage = (error: unknown): string | null =>
  error instanceof HttpError && !isInvalidRequest(error) && error.message.trim() !== '' ? error.message : null;

const violationsOf = (error: ApiError): Violation[] =>
  error.violations.map((violation) => ({
    path: violation.path ?? '',
    code: violation.code,
    message: violation.message ?? '',
  }));

const machinesOf = (error: ApiError): number | null => {
  const match = /^(\d+)\s/.exec(error.message.trim());
  return match ? Number(match[1]) : null;
};

const otherOf = (error: unknown): SaveFailure => ({
  kind: 'other',
  code: refusalCodeOf(error),
  status: statusOf(error),
  message: serverMessage(error),
  retryable: !(error instanceof ContractError) && isRetryableSaveError(error),
});

const violationSourceOf = (code: RefusalCode | null): ViolationSource => {
  if (code === 'LIBRARY_RULES' || code === 'REGISTRY_RULES') return code;
  return 'SCHEMA';
};

export const saveFailureOf = (error: unknown): SaveFailure => {
  if (!(error instanceof ApiError)) return otherOf(error);
  const code = refusalCodeOf(error);
  const message = serverMessage(error);
  if (code === 'VERSION_CONFLICT') {
    return { kind: 'conflict', expectedVersion: error.expectedVersion, currentVersion: error.currentVersion, message };
  }
  if (code === 'LIBRARY_RULES' || code === 'REGISTRY_RULES' || (error.status === STATUS_UNPROCESSABLE && error.violations.length > 0)) {
    return { kind: 'violations', source: violationSourceOf(code), violations: violationsOf(error), message };
  }
  if (code === 'UNKNOWN_ITEM') return { kind: 'unknownItem', keys: error.keys, message };
  if (code === 'IN_USE') return { kind: 'inUse', machines: machinesOf(error), message };
  if (code === 'REASON_TOO_SHORT') return { kind: 'reason', problem: 'TOO_SHORT', message };
  if (code === 'REASON_TOO_LONG') return { kind: 'reason', problem: 'TOO_LONG', message };
  if (error.status === STATUS_FORBIDDEN) return { kind: 'forbidden', message };
  if (error.status === STATUS_NOT_FOUND) return { kind: 'notFound', message };
  return otherOf(error);
};

export const needsFreshCopy = (failure: SaveFailure): boolean =>
  failure.kind === 'conflict' ||
  failure.kind === 'notFound' ||
  failure.kind === 'unknownItem' ||
  (failure.kind === 'other' && (failure.code === 'NOT_IN_DATABASE' || failure.code === 'LIBRARY_ARCHIVED'));

export const isAmbiguousFailure = (error: unknown): boolean => error instanceof ContractError || isRetryableSaveError(error);
